import React, { useState, useEffect } from 'react';
import { Box, Button, Slider, Typography, Tooltip, IconButton, TextField, InputAdornment } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from 'react-i18next';

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

interface TimeSliderPanelProps {
  minDate: string | null;
  maxDate: string | null;
  selectedDate: string | null;
  onDateChange: (date: string | null) => void;
  isDarkMode?: boolean;
}

const TimeSliderPanel: React.FC<TimeSliderPanelProps> = ({
  minDate,
  maxDate,
  selectedDate,
  onDateChange,
  isDarkMode
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Convert date string (YYYY-MM-DD) to timestamp
  const dateToTimestamp = (dateStr: string): number => {
    return new Date(dateStr).getTime();
  };

  // Convert timestamp to date string (YYYY-MM-DD)
  const timestampToDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  if (!minDate || !maxDate) {
    return null;
  }

  const minTime = dateToTimestamp(minDate);
  const maxTime = dateToTimestamp(maxDate);
  // If selectedDate is null, default to maxTime (show everything up to now)
  const currentTime = selectedDate ? dateToTimestamp(selectedDate) : maxTime;

  const handleChange = (event: Event, newValue: number | number[]) => {
    const newDate = timestampToDate(newValue as number);
    onDateChange(newDate);
  };

  const handleDateInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    // Validate date is within range
    if (newDate >= minDate && newDate <= maxDate) {
      onDateChange(newDate);
    } else if (newDate > maxDate) {
      onDateChange(maxDate);
    } else if (newDate < minDate) {
      onDateChange(minDate);
    }
  };

  const handleReset = () => {
    onDateChange(null); // Reset to show all (or max date)
    setIsPlaying(false);
  };

  const shiftDateByDays = (days: number) => {
    setIsPlaying(false);
    const nextTime = Math.max(minTime, Math.min(maxTime, currentTime + days * DAY_IN_MILLISECONDS));
    onDateChange(timestampToDate(nextTime));
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  // Animation loop
  useEffect(() => {
    let animationFrame: number;
    let lastTime = Date.now();
    
    const animate = () => {
      if (!isPlaying) return;
      
      const now = Date.now();
      const dt = now - lastTime;
      
      if (dt > 100) { // Update every 100ms
        lastTime = now;
        
        // Advance time by 1 month per tick (approx)
        // Calculate step size based on total range to make it traverse in ~10 seconds
        const totalRange = maxTime - minTime;
        const step = totalRange / 100; 
        
        let nextTime = currentTime + step;
        if (nextTime >= maxTime) {
          nextTime = maxTime;
          setIsPlaying(false);
        }
        
        onDateChange(timestampToDate(nextTime));
      }
      
      animationFrame = requestAnimationFrame(animate);
    };

    if (isPlaying) {
      animationFrame = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [isPlaying, currentTime, maxTime, minTime, onDateChange]);

  const textColor = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)';
  const secondaryTextColor = isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title={t(isPlaying ? 'graph.timeline.pause' : 'graph.timeline.play')}>
          <IconButton 
            onClick={togglePlay} 
            size="small" 
            sx={{ 
              color: isDarkMode ? '#90caf9' : '#1976d2',
              border: `1px solid ${isDarkMode ? 'rgba(144, 202, 249, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
              borderRadius: '8px',
              padding: '6px'
            }}
          >
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={t('graph.timeline.reset')}>
          <IconButton 
            onClick={handleReset} 
            size="small" 
            sx={{ 
              color: textColor,
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
              borderRadius: '8px',
              padding: '6px'
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button size="small" variant="outlined" disabled={currentTime <= minTime}
          onClick={() => shiftDateByDays(-7)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
          {t('graph.timeline.backSevenDays')}
        </Button>
        <Button size="small" variant="outlined" disabled={currentTime >= maxTime}
          onClick={() => shiftDateByDays(7)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
          {t('graph.timeline.forwardSevenDays')}
        </Button>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, flexBasis: { xs: '100%', sm: 280 }, minWidth: 0, order: { xs: 1, sm: 0 } }}>
        <Typography variant="caption" sx={{ color: secondaryTextColor, whiteSpace: 'nowrap', minWidth: '70px' }}>
          {minDate}
        </Typography>
        
        <Slider
          aria-label={t('graph.timeFilter')}
          value={currentTime}
          min={minTime}
          max={maxTime}
          step={DAY_IN_MILLISECONDS}
          onChange={handleChange}
          size="medium"
          sx={{
            color: isDarkMode ? '#90caf9' : '#1976d2',
            height: 6,
            padding: '13px 0',
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              backgroundColor: isDarkMode ? '#90caf9' : '#1976d2',
              border: '2px solid currentColor',
              '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: 'inherit',
              },
              '&:before': {
                display: 'none',
              },
            },
            '& .MuiSlider-rail': {
              opacity: 0.3,
            },
          }}
        />
        
        <Typography variant="caption" sx={{ color: secondaryTextColor, whiteSpace: 'nowrap', minWidth: '70px', textAlign: 'right' }}>
          {maxDate}
        </Typography>
      </Box>

      <TextField
        type="date"
        value={selectedDate || maxDate}
        onChange={handleDateInputChange}
        variant="outlined"
        size="small"
        sx={{
          width: 150,
          flexShrink: 0,
          '& .MuiOutlinedInput-root': {
            color: textColor,
            borderRadius: '8px',
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            height: '36px',
            '& fieldset': {
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#90caf9' : '#1976d2',
            },
          },
          '& input': {
            padding: '8px 10px',
            fontSize: '0.875rem'
          },
          '& input::-webkit-calendar-picker-indicator': {
            filter: isDarkMode ? 'invert(1)' : 'none',
            cursor: 'pointer'
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ marginRight: '4px' }}>
              <CalendarTodayIcon sx={{ color: secondaryTextColor, fontSize: '1rem' }} />
            </InputAdornment>
          ),
          inputProps: {
            'aria-label': t('common.fields.date'),
            min: minDate,
            max: maxDate
          }
        }}
      />
    </Box>
  );
};

export default TimeSliderPanel;
